import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Root } from 'react-dom/client'
import { HomeworkComment, HomeworkTocItem, parseAnchorJson } from '../../services/homeworkApi'
import { mountMermaidBlocks, unmountMermaidRoots } from '../learning/HtmlWithMermaid'
import HomeworkCommentHoverCard from './HomeworkCommentHoverCard'
import { ensureHeadingIds } from '../../utils/homeworkToc'
import { DrawioBlockValue, parseDrawioBlock } from './drawio/drawioHtml'
import './drawio/DrawioEmbedModal.css'

type Props = {
  html: string
  toc?: HomeworkTocItem[] | null
  comments: HomeworkComment[]
  mode: 'view' | 'annotate'
  onCreateTextComment?: (payload: {
    quote: string
    prefix: string
    suffix: string
    body: string
  }) => Promise<void> | void
  onCreateImageComment?: (payload: {
    imageSrc: string
    xPct: number
    yPct: number
    wPct: number
    hPct: number
    body: string
  }) => Promise<void> | void
  activeCommentId?: number | null
  onSelectComment?: (id: number) => void
  /** Hide built-in TOC (when parent page renders TOC separately) */
  hideToc?: boolean
  canReply?: boolean
  onReplyToComment?: (parentId: number, body: string) => Promise<void> | void
  /** Extra classes for the HTML body (e.g. larger text) */
  bodyClassName?: string
  /** Called after headings are given ids in the rendered DOM */
  onTocReady?: (toc: HomeworkTocItem[]) => void
  /** Allow click-to-edit draw.io previews (student returned/draft) */
  canEditDiagram?: boolean
  onEditDiagram?: (value: DrawioBlockValue) => void
}

function getContextAround(text: string, quote: string): { prefix: string; suffix: string } {
  const idx = text.indexOf(quote)
  if (idx < 0) return { prefix: '', suffix: '' }
  return {
    prefix: text.slice(Math.max(0, idx - 40), idx),
    suffix: text.slice(idx + quote.length, idx + quote.length + 40),
  }
}

export default function HomeworkAnnotationViewer({
  html,
  toc,
  comments,
  mode,
  onCreateTextComment,
  onCreateImageComment,
  activeCommentId,
  onSelectComment,
  hideToc = false,
  canReply = true,
  onReplyToComment,
  bodyClassName = '',
  onTocReady,
  canEditDiagram = false,
  onEditDiagram,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mermaidRootsRef = useRef<Root[]>([])
  const onEditDiagramRef = useRef(onEditDiagram)
  onEditDiagramRef.current = onEditDiagram
  const [draftBody, setDraftBody] = useState('')
  const [pendingText, setPendingText] = useState<{
    quote: string
    prefix: string
    suffix: string
  } | null>(null)
  const [pendingImage, setPendingImage] = useState<{
    imageSrc: string
    xPct: number
    yPct: number
    wPct: number
    hPct: number
  } | null>(null)
  const dragRef = useRef<{
    img: HTMLImageElement
    startX: number
    startY: number
  } | null>(null)
  const hoverCloseTimer = useRef<number | null>(null)
  const hoverPinnedRef = useRef(false)
  const hoverIdRef = useRef<number | null>(null)
  const [hover, setHover] = useState<{
    commentId: number
    rect: DOMRect
  } | null>(null)
  const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({})
  const [hoverPinned, setHoverPinned] = useState(false)

  useEffect(() => {
    hoverPinnedRef.current = hoverPinned
  }, [hoverPinned])

  useEffect(() => {
    hoverIdRef.current = hover?.commentId ?? null
  }, [hover?.commentId])

  const rootComments = useMemo(
    () => comments.filter((c) => !c.parentId && c.anchorType),
    [comments]
  )

  const commentsById = useMemo(() => {
    const map = new Map<number, HomeworkComment>()
    for (const c of comments) map.set(c.id, c)
    return map
  }, [comments])

  const repliesOf = useCallback(
    (id: number) => comments.filter((c) => Number(c.parentId) === id),
    [comments]
  )

  const openHover = useCallback((commentId: number, el: Element) => {
    // Đang soạn reply → không nhảy sang comment khác
    if (
      hoverPinnedRef.current &&
      hoverIdRef.current != null &&
      hoverIdRef.current !== commentId
    ) {
      return
    }
    if (hoverCloseTimer.current) {
      window.clearTimeout(hoverCloseTimer.current)
      hoverCloseTimer.current = null
    }
    setHover({ commentId, rect: el.getBoundingClientRect() })
  }, [])

  const keepHover = useCallback(() => {
    if (hoverCloseTimer.current) {
      window.clearTimeout(hoverCloseTimer.current)
      hoverCloseTimer.current = null
    }
  }, [])

  const scheduleCloseHover = useCallback(() => {
    if (hoverPinnedRef.current) return
    if (hoverCloseTimer.current) window.clearTimeout(hoverCloseTimer.current)
    hoverCloseTimer.current = window.setTimeout(() => {
      if (hoverPinnedRef.current) return
      setHover((prev) => {
        if (!prev) return null
        const draft = replyDrafts[prev.commentId]?.trim()
        if (draft) return prev
        return null
      })
    }, 220)
  }, [replyDrafts])

  const applyHighlights = useCallback(() => {
    const root = containerRef.current
    if (!root) return
    root.querySelectorAll('mark[data-hw-cmt]').forEach((el) => {
      const parent = el.parentNode
      if (!parent) return
      while (el.firstChild) parent.insertBefore(el.firstChild, el)
      parent.removeChild(el)
    })

    for (const c of rootComments) {
      if (c.visibility === 'rejected') continue
      const anchor = parseAnchorJson(c.anchorJson)
      if (!anchor || !('quote' in anchor) || !anchor.quote) continue
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
      let node: Node | null
      while ((node = walker.nextNode())) {
        if ((node.parentElement as HTMLElement | null)?.closest('.mermaid-html-mount')) continue
        const text = node.textContent || ''
        const idx = text.indexOf(anchor.quote)
        if (idx < 0) continue
        const range = document.createRange()
        range.setStart(node, idx)
        range.setEnd(node, idx + anchor.quote.length)
        const mark = document.createElement('mark')
        mark.setAttribute('data-hw-cmt', String(c.id))
        mark.className =
          activeCommentId === c.id
            ? 'bg-amber-300/80 cursor-pointer'
            : c.visibility === 'pending_approval'
              ? 'bg-violet-200/70 cursor-pointer'
              : 'bg-yellow-200/70 cursor-pointer'
        mark.onclick = (e) => {
          e.stopPropagation()
          onSelectComment?.(c.id)
        }
        mark.onmouseenter = () => openHover(c.id, mark)
        mark.onmouseleave = () => scheduleCloseHover()
        try {
          range.surroundContents(mark)
        } catch {
          /* overlapping */
        }
        break
      }
    }
  }, [rootComments, activeCommentId, onSelectComment, openHover, scheduleCloseHover])

  useEffect(() => {
    const root = containerRef.current
    if (!root) return
    unmountMermaidRoots(mermaidRootsRef.current)
    mermaidRootsRef.current = []
    root.innerHTML = html || ''
    const nextToc = ensureHeadingIds(root)
    onTocReady?.(nextToc)
    mermaidRootsRef.current = mountMermaidBlocks(root, 'hw-mmd')
    applyHighlights()

    const cleanups: Array<() => void> = []
    root.querySelectorAll('.hw-drawio').forEach((node) => {
      const el = node as HTMLElement
      if (canEditDiagram && onEditDiagramRef.current) {
        el.classList.add('hw-drawio--editable')
        const hint = el.querySelector('.hw-drawio-hint')
        if (hint) hint.textContent = 'Nhấn vào sơ đồ để chỉnh sửa'
        const onClick = (e: Event) => {
          e.preventDefault()
          e.stopPropagation()
          const value = parseDrawioBlock(el)
          if (value) onEditDiagramRef.current?.(value)
        }
        el.addEventListener('click', onClick)
        cleanups.push(() => el.removeEventListener('click', onClick))
      } else {
        el.classList.remove('hw-drawio--editable')
        const hint = el.querySelector('.hw-drawio-hint')
        if (hint) {
          hint.textContent = canEditDiagram
            ? 'Nhấn vào sơ đồ để chỉnh sửa'
            : 'Sơ đồ quy trình (chỉ xem)'
        }
      }
    })

    return () => {
      cleanups.forEach((fn) => fn())
      unmountMermaidRoots(mermaidRootsRef.current)
      mermaidRootsRef.current = []
    }
  }, [html, applyHighlights, onTocReady, canEditDiagram])

  useEffect(() => {
    if (!activeCommentId || !containerRef.current) return
    const el = containerRef.current.querySelector(`[data-hw-cmt="${activeCommentId}"]`)
    const box = containerRef.current.querySelector(`[data-hw-img-cmt="${activeCommentId}"]`)
    ;(el || box)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [activeCommentId])

  const onMouseUp = () => {
    if (mode !== 'annotate' || !onCreateTextComment) return
    const sel = window.getSelection()
    if (!sel || sel.isCollapsed || !containerRef.current) return
    if (!containerRef.current.contains(sel.anchorNode)) return
    const quote = sel.toString().trim()
    if (quote.length < 2) return
    const full = containerRef.current.innerText || ''
    const ctx = getContextAround(full, quote)
    setPendingImage(null)
    setPendingText({ quote, ...ctx })
    setDraftBody('')
  }

  const onImgMouseDown = (e: React.MouseEvent) => {
    if (mode !== 'annotate' || !onCreateImageComment) return
    const target = e.target as HTMLElement
    if (target.tagName !== 'IMG') return
    // Draw.io previews: prefer text comments near title; skip region drag
    if (target.closest('.hw-drawio')) return
    e.preventDefault()
    const img = target as HTMLImageElement
    const rect = img.getBoundingClientRect()
    dragRef.current = {
      img,
      startX: ((e.clientX - rect.left) / rect.width) * 100,
      startY: ((e.clientY - rect.top) / rect.height) * 100,
    }
  }

  const onImgMouseUp = (e: React.MouseEvent) => {
    if (!dragRef.current || mode !== 'annotate') return
    const { img, startX, startY } = dragRef.current
    dragRef.current = null
    const rect = img.getBoundingClientRect()
    const endX = ((e.clientX - rect.left) / rect.width) * 100
    const endY = ((e.clientY - rect.top) / rect.height) * 100
    const xPct = Math.max(0, Math.min(startX, endX))
    const yPct = Math.max(0, Math.min(startY, endY))
    const wPct = Math.max(3, Math.abs(endX - startX))
    const hPct = Math.max(3, Math.abs(endY - startY))
    if (wPct < 4 && hPct < 4) return
    setPendingText(null)
    setPendingImage({
      imageSrc: img.currentSrc || img.src,
      xPct,
      yPct,
      wPct: Math.min(100 - xPct, wPct),
      hPct: Math.min(100 - yPct, hPct),
    })
    setDraftBody('')
  }

  const submitDraft = async () => {
    const body = draftBody.trim()
    if (!body) return
    if (pendingText && onCreateTextComment) {
      await onCreateTextComment({ ...pendingText, body })
    } else if (pendingImage && onCreateImageComment) {
      await onCreateImageComment({ ...pendingImage, body })
    }
    setPendingText(null)
    setPendingImage(null)
    setDraftBody('')
    window.getSelection()?.removeAllRanges()
  }

  const imageOverlays = rootComments
    .map((c) => {
      const a = parseAnchorJson(c.anchorJson)
      if (!a || !('imageSrc' in a)) return null
      return { comment: c, anchor: a }
    })
    .filter(Boolean) as Array<{
    comment: HomeworkComment
    anchor: { imageSrc: string; xPct: number; yPct: number; wPct: number; hPct: number }
  }>

  return (
    <div className="space-y-4">
      {!!toc?.length && !hideToc && (
        <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-low p-3">
          <p className="text-xs font-semibold text-m2-on-surface-variant mb-2">Mục lục</p>
          <ul className="space-y-1 text-sm">
            {toc.map((t) => (
              <li key={t.id} style={{ paddingLeft: `${(t.level - 1) * 12}px` }}>
                <a
                  href={`#${t.id}`}
                  className="text-m2-primary hover:underline"
                  onClick={(e) => {
                    e.preventDefault()
                    document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth' })
                  }}
                >
                  {t.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="relative">
        <div
          ref={containerRef}
          className={`prose max-w-none homework-annotation-body html-media-center ${bodyClassName || 'prose-sm'}`}
          onMouseUp={onMouseUp}
          onMouseDown={onImgMouseDown}
          onMouseUpCapture={onImgMouseUp}
        />
        <ImageOverlayLayer
          containerRef={containerRef}
          overlays={imageOverlays}
          activeCommentId={activeCommentId}
          onSelectComment={onSelectComment}
          onHoverEnter={(id, el) => openHover(id, el)}
          onHoverLeave={scheduleCloseHover}
        />
      </div>

      {hover && commentsById.get(hover.commentId) && (
        <HomeworkCommentHoverCard
          comment={commentsById.get(hover.commentId)!}
          replies={repliesOf(hover.commentId)}
          open
          anchorRect={hover.rect}
          canReply={canReply && Boolean(onReplyToComment)}
          draft={replyDrafts[hover.commentId] || ''}
          onDraftChange={(value) =>
            setReplyDrafts((prev) => ({ ...prev, [hover.commentId]: value }))
          }
          pinned={hoverPinned}
          onPinnedChange={setHoverPinned}
          onClose={() => {
            setHoverPinned(false)
            setHover(null)
          }}
          onKeepOpen={keepHover}
          onReply={
            onReplyToComment
              ? async (body) => {
                  const id = hover.commentId
                  await onReplyToComment(id, body)
                  setReplyDrafts((prev) => {
                    const next = { ...prev }
                    delete next[id]
                    return next
                  })
                  setHoverPinned(false)
                }
              : undefined
          }
        />
      )}

      {(pendingText || pendingImage) && (
        <div className="sticky bottom-2 z-10 rounded-lg border border-m2-primary/40 bg-white p-3 shadow-lg">
          <p className="text-xs text-m2-on-surface-variant mb-2">
            {pendingText
              ? `Comment trên: “${pendingText.quote.slice(0, 80)}${pendingText.quote.length > 80 ? '…' : ''}”`
              : 'Comment trên vùng ảnh đã chọn'}
          </p>
          <textarea
            className="w-full border border-m2-outline-variant rounded-md p-2 text-sm mb-2"
            rows={3}
            value={draftBody}
            onChange={(e) => setDraftBody(e.target.value)}
            placeholder="Nhập nhận xét..."
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded-md border"
              onClick={() => {
                setPendingText(null)
                setPendingImage(null)
              }}
            >
              Hủy
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded-md bg-m2-primary text-white"
              onClick={() => void submitDraft()}
            >
              Gửi comment
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function ImageOverlayLayer({
  containerRef,
  overlays,
  activeCommentId,
  onSelectComment,
  onHoverEnter,
  onHoverLeave,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>
  overlays: Array<{
    comment: HomeworkComment
    anchor: { imageSrc: string; xPct: number; yPct: number; wPct: number; hPct: number }
  }>
  activeCommentId?: number | null
  onSelectComment?: (id: number) => void
  onHoverEnter?: (id: number, el: Element) => void
  onHoverLeave?: () => void
}) {
  const [boxes, setBoxes] = useState<
    Array<{ id: number; left: number; top: number; width: number; height: number; pending: boolean }>
  >([])

  useEffect(() => {
    const root = containerRef.current
    if (!root) return
    const next: typeof boxes = []
    const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[]
    for (const { comment, anchor } of overlays) {
      const img =
        imgs.find((i) => (i.currentSrc || i.src) === anchor.imageSrc) ||
        imgs.find((i) => (i.currentSrc || i.src).includes(anchor.imageSrc.split('/').pop() || ''))
      if (!img) continue
      const parent = root.getBoundingClientRect()
      const r = img.getBoundingClientRect()
      const left = r.left - parent.left + (anchor.xPct / 100) * r.width
      const top = r.top - parent.top + (anchor.yPct / 100) * r.height
      next.push({
        id: comment.id,
        left,
        top,
        width: (anchor.wPct / 100) * r.width,
        height: (anchor.hPct / 100) * r.height,
        pending: comment.visibility === 'pending_approval',
      })
    }
    setBoxes(next)
  }, [overlays, containerRef])

  return (
    <div className="pointer-events-none absolute inset-0">
      {boxes.map((b) => (
        <div
          key={b.id}
          data-hw-img-cmt={b.id}
          className={`absolute border-2 pointer-events-auto cursor-pointer ${
            activeCommentId === b.id
              ? 'border-amber-500 bg-amber-400/20'
              : b.pending
                ? 'border-violet-500 bg-violet-400/20'
                : 'border-rose-500 bg-rose-400/15'
          }`}
          style={{ left: b.left, top: b.top, width: b.width, height: b.height }}
          onClick={() => onSelectComment?.(b.id)}
          onMouseEnter={(e) => onHoverEnter?.(b.id, e.currentTarget)}
          onMouseLeave={() => onHoverLeave?.()}
        />
      ))}
    </div>
  )
}
